import { Bell, BellOff, Check, X, Loader2, Smartphone, Info } from 'lucide-react';
import { Button } from './ui/button';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { toast } from 'sonner';
import { useState } from 'react';

export default function PushNotificationToggle({ className = '' }) {
  const {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    testNotification,
    deviceType,
    isPWA
  } = usePushNotifications();

  const [showHelp, setShowHelp] = useState(false);

  const handleToggle = async () => {
    if (isSubscribed) {
      const success = await unsubscribe();
      if (success) {
        toast.success('Notifications désactivées');
      } else {
        toast.error('Erreur lors de la désactivation');
      }
    } else {
      const success = await subscribe();
      if (success) {
        toast.success('Notifications activées! Vous recevrez une alerte pour chaque nouvelle commande.');
      } else if (permission === 'denied') {
        toast.error('Notifications bloquées. Veuillez les autoriser dans les paramètres de votre navigateur.');
      } else {
        toast.error('Erreur lors de l\'activation des notifications');
      }
    }
  };

  const handleTest = async () => {
    const success = await testNotification();
    if (success) {
      toast.success('Notification de test envoyée!');
    } else {
      toast.error(error || 'Erreur lors de l\'envoi du test');
    }
  };

  // Show help for unsupported devices
  if (!isSupported) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center gap-2 text-sm text-orange-400 cursor-pointer" onClick={() => setShowHelp(!showHelp)}>
          <Smartphone className="h-4 w-4" />
          <span>Notifications</span>
          <Info className="h-3 w-3" />
        </div>
        
        {showHelp && (
          <div className="mt-2 p-3 bg-gray-800 rounded-lg text-xs text-gray-300 space-y-2">
            {deviceType === 'ios' ? (
              <>
                <p className="font-semibold text-orange-400">📱 Sur iPhone/iPad :</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Ouvrez ce site dans <strong>Safari</strong></li>
                  <li>Appuyez sur <strong>Partager</strong> (icône carré avec flèche)</li>
                  <li>Sélectionnez <strong>"Sur l'écran d'accueil"</strong></li>
                  <li>Ouvrez l'app depuis l'écran d'accueil</li>
                  <li>Les notifications seront disponibles!</li>
                </ol>
              </>
            ) : deviceType === 'android' ? (
              <>
                <p className="font-semibold text-orange-400">📱 Sur Android :</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Ouvrez ce site dans <strong>Chrome</strong></li>
                  <li>Appuyez sur <strong>⋮</strong> (menu)</li>
                  <li>Sélectionnez <strong>"Installer l'application"</strong></li>
                  <li>Ouvrez l'app installée</li>
                  <li>Activez les notifications!</li>
                </ol>
              </>
            ) : (
              <>
                <p className="font-semibold text-orange-400">💻 Sur ordinateur :</p>
                <p>Utilisez Chrome, Firefox ou Edge pour les notifications.</p>
                <p>Safari Mac ne supporte pas encore les notifications push.</p>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <div className="flex items-center gap-3">
        <Button
          onClick={handleToggle}
          disabled={loading}
          variant={isSubscribed ? "default" : "outline"}
          size="sm"
          className={`flex items-center gap-2 ${isSubscribed ? 'bg-green-600 hover:bg-green-700' : ''}`}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isSubscribed ? (
            <>
              <Bell className="h-4 w-4" />
              <span className="hidden sm:inline">Notifications activées</span>
              <Check className="h-3 w-3" />
            </>
          ) : (
            <>
              <BellOff className="h-4 w-4" />
              <span className="hidden sm:inline">Activer les notifications</span>
            </>
          )}
        </Button>
      </div>

      {permission === 'denied' && (
        <span className="text-xs text-red-500">
          Bloquées dans le navigateur. Allez dans Paramètres → Notifications pour les autoriser.
        </span>
      )}
    </div>
  );
}

